// WS listener do dashboard "Tempo real". Qualquer evento de ciclo de vida de
// conversa no namespace /inbox invalida a tag RealtimeCounts (refetch throttled).
//
// Por que invalidateTags e não patch manual: os contadores são agregados por
// status no back (escopo por papel), derivar no front exigiria espelhar essa
// regra. Como invalidateTags só refaz queries com subscription ativa, isto só
// refetcha quando o DashboardPage está montado — seguro app-wide.
//
// Por que throttle leading+trailing de 5s (e não debounce): o endpoint
// /v1/analytics/realtime/counts ganhou um micro-cache de 5s no backend. Em pico
// (10-50 eventos/s × N dashboards abertos), invalidar por evento vira rajada de
// GETs que o cache absorveria à toa. O throttle casa com esse TTL: o primeiro
// evento refetcha na hora (leading) e a rajada seguinte coalesce num único
// refetch ao fim da janela (trailing) — no máximo 1 refetch por 5s por aba.
//
// Setup: anexado uma única vez no AppProviders via attachDashboardRealtimeListener().
import type { AnyAction } from '@reduxjs/toolkit';
import { listenerMiddleware } from '@/app/store/middleware';
import { throttle, type Throttled } from '@/lib/utils/throttle';
import { dashboardApi } from './api';

// Mesma justificativa de ws-listeners.ts do inbox: o Dispatch genérico do
// listenerMiddleware não aceita os ThunkActions tipados de createApi sem ciclo.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type LooseDispatch = (action: any) => any;

const REALTIME_EVENTS = new Set<string>([
  'inbox/conversation.created',
  'inbox/conversation.status.changed',
  'inbox/conversation.claimed',
  'inbox/conversation.released',
  'inbox/conversation.assigned',
]);

// Casado com o TTL do micro-cache do endpoint realtime/counts no backend.
const THROTTLE_MS = 5000;

/**
 * Registra o listener de invalidação throttled (leading+trailing, 5s). Em rajada
 * de eventos, refetcha na hora no primeiro e no máximo uma vez ao fim da janela.
 */
export function attachDashboardRealtimeListener(): void {
  // Construído lazy na primeira emissão: o dispatch do store é estável entre
  // chamadas, então capturá-lo uma vez basta e mantém o throttle vivo entre eventos.
  let throttled: Throttled | null = null;

  listenerMiddleware.startListening({
    predicate: (action) => REALTIME_EVENTS.has((action as AnyAction).type),
    effect: (_action, listenerApi) => {
      const dispatch = listenerApi.dispatch as LooseDispatch;
      throttled ??= throttle(
        () => dispatch(dashboardApi.util.invalidateTags(['RealtimeCounts'])),
        THROTTLE_MS,
      );
      throttled();
    },
  });
}
